import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import { articleIsReadyForPickup } from '@/models/article';
import {
  RequestWithRelations,
  useCompletePendingClientPickup,
  useRequestDocuments,
} from '@/models/request';
import { PickUpReceiptDrawer } from '@/routes/Requests/Request/Article/components/ArticleActions/shared/PickUpReceiptDrawer';

const CompleteDeliveryPendingClientAction = ({
  request,
  onActionDone,
}: {
  request: RequestWithRelations;
  onActionDone: () => void;
}) => {
  const { t } = useLingui();

  const [isPickUpReceiptDrawerOpen, setIsPickUpReceiptDrawerOpen] = useState(false);
  const [apiError, setApiError] = useState('');

  const {
    mutateAsync: completeClientPickup,
    isPending: isPendingCompleteClientPickup,
    isSuccess: isSuccessCompleteClientPickup,
  } = useCompletePendingClientPickup({ requestId: request.id });

  const handleCompletePickUp = async () => {
    try {
      await completeClientPickup();
      setIsPickUpReceiptDrawerOpen(false);
      onActionDone();
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  const isLoadingCompletePickUp = isPendingCompleteClientPickup || isSuccessCompleteClientPickup;

  const exampleArticleReadyForPickUp = request.articles.find(articleIsReadyForPickup)!;

  const { data: generatedDocuments = [] } = useRequestDocuments({
    requestId: request.id,
  });

  const alreadySignedPickUpReceipt =
    request.articles.length === 1
      ? generatedDocuments.find((document) => document.type === 'pick-up-receipt')
      : undefined;

  const shouldSignPickUpReceipt =
    exampleArticleReadyForPickUp.step?.step === 'transit' &&
    exampleArticleReadyForPickUp.step.config.clientPickUpSignature?.enabled &&
    !alreadySignedPickUpReceipt;

  return (
    <>
      <Footer>
        {!!apiError && !isPickUpReceiptDrawerOpen && <FooterError>{apiError}</FooterError>}
        <FooterButtons>
          <Button
            variant="primary"
            onPress={() => {
              if (shouldSignPickUpReceipt) {
                setIsPickUpReceiptDrawerOpen(true);
              } else {
                handleCompletePickUp();
              }
            }}
            isLoading={isLoadingCompletePickUp && !isPickUpReceiptDrawerOpen}
          >
            {shouldSignPickUpReceipt ? (
              <Trans id="request.delivery-pending-client.signature">
                Proceed to pickup signature
              </Trans>
            ) : (
              <Trans id="request.delivery-pending-client.conclude">Client pickup done</Trans>
            )}
          </Button>
        </FooterButtons>
      </Footer>
      <PickUpReceiptDrawer
        isOpen={isPickUpReceiptDrawerOpen}
        setIsOpen={setIsPickUpReceiptDrawerOpen}
        request={request}
        onCompletePickUp={handleCompletePickUp}
        isLoadingCompletePickUp={isLoadingCompletePickUp}
        completePickUpError={apiError}
      />
    </>
  );
};

export default CompleteDeliveryPendingClientAction;
